import { produce } from "immer";

export class Store<TStoreValue> {
  id = Date.now().toString();
  listeners: Set<() => void> = new Set();
  constructor(private value: TStoreValue) {}

  get(): TStoreValue {
    return this.value;
  }

  set(setter: (value: TStoreValue) => void): void {
    this.value = produce(this.value, (draft) => {
      setter(draft as TStoreValue);
    });
    this.listeners.forEach((listener) => listener());
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  createDerivedSnapshot<TSnapshot>(
    getter: (value: TStoreValue) => TSnapshot,
    isEqual: (a: TSnapshot, b: TSnapshot) => boolean,
  ) {
    let snapshot = getter(this.value);

    const getSnapshot = () => snapshot;

    const subscribe = (listener: () => void) =>
      this.subscribe(() => {
        const next = getter(this.value);
        if (!isEqual(snapshot, next)) {
          snapshot = next;
          listener();
        }
      });

    return { getSnapshot, subscribe };
  }
}

export function isPropertyKey(input: unknown): input is PropertyKey {
  return typeof input === "symbol" || typeof input === "number" || typeof input === "string";
}

export function isObjectPropertyKey<TObject extends object>(
  obj: TObject,
  input: unknown,
): input is keyof TObject {
  return isPropertyKey(input) && input in obj;
}

type MappedObject<T extends Record<string, any>, R> = {
  [K in keyof T]: R;
};

/**
 * Maps the values of an object while preserving all original keys.
 * * @param obj The input object.
 * @param mapFn A function that receives the key and value, and returns the new value.
 * @returns A new object with the same keys but transformed values.
 */
export function mapObject<T extends Record<string, any>, R>(
  obj: T,
  mapFn: (key: keyof T, value: T[keyof T]) => R,
): MappedObject<T, R> {
  const entries = Object.entries(obj);

  const mappedEntries = entries.map(([key, value]) => {
    // The key must be cast to keyof T to satisfy the mapFn signature
    const newKey = key as keyof T;
    const newValue = mapFn(newKey, value);

    // Return the [key, newValue] pair
    return [key, newValue];
  });

  // Object.fromEntries returns a generic Record<string, any>.
  // We use 'as MappedObject<T, R>' to enforce the calculated type safety.
  return Object.fromEntries(mappedEntries) as MappedObject<T, R>;
}
